import { getFileBaseName } from '@/utils/getFileBaseName';
import type { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  createDocumentFileObject,
  deleteDocumentFileObject,
  getDocumentFileUploadUrl,
  setStoredFileActive,
  updateDocumentFileObject,
  uploadDocumentFileBinary,
} from '../../api/documents';
import { useOptionalUploadRegistry, type ActiveUpload } from '../../context/UploadRegistryContext';
import { useInvalidateDocumentFileAll } from '../../hooks';
import type { DocumentFile, DocumentUploadStatus } from '../../types';
import { downloadDocumentFile, openDocumentFileToView } from '../../utils/documentFileActions';
import type {
  CreateResult,
  FileObjectRow,
  FileObjectRowsActions,
  FileObjectRowsState,
  FileObjectSubmitRow,
  FileObjectValues,
  InitialFileObjectRow,
} from './types';
import { newUUID } from '@/utils/uuid';


// useFileObjectRows coordinates row editing, validation, and upload lifecycle for document file objects.
export function useFileObjectRows(
  options: UseFileObjectRowsOptions = {},
  fileName: string,
): { state: FileObjectRowsState; actions: FileObjectRowsActions } {
  const { t } = useTranslation();
  const { fileId, saveImmediately } = options;

  const registry = useOptionalUploadRegistry();
  const invalidateFileQuery = useInvalidateDocumentFileAll();

  // One AbortController per active upload, keyed by rowId.
  // Allows delete actions to cancel in-flight XHR requests.
  const uploadAbortControllersRef = useRef<Map<string, AbortController>>(new Map());

  const [inner, dispatch] = useReducer(reducer, options.initialRows, createReducerState);

  // Keep the latest rows in a ref so async callbacks never rely on stale closures.
  const rowsRef = useRef<FileObjectRow[]>(inner.rows);
  useEffect(() => {
    rowsRef.current = inner.rows;
  }, [inner.rows]);

  // Subscribe to registry changes for this file so progress updates from an
  // ongoing upload (started by a previous hook instance) are reflected in the
  // local rows after the panel is closed and reopened.
  useEffect(() => {
    if (!fileId || !registry) return;

    return registry.subscribe(() => {
      registry.getForFile(fileId).forEach((upload) => {
        const row = rowsRef.current.find((r) => r.values.objectId === upload.objectId);
        if (!row) return;
        if (row.uploadStatus === upload.status && row.uploadProgress === upload.progress) return;
        dispatch({
          type: 'SET_UPLOAD_STATUS',
          rowId: row.rowId,
          status: upload.status,
          progress: upload.progress,
          objectId: upload.objectId,
        });
      });
    });
  }, [fileId, registry]);

  const addRowWithLanguage = useCallback((languageId: number | null) => {
    dispatch({ type: 'ADD_ROW', languageId });
  }, []);

  const removeRow = useCallback((rowId: string) => {
    dispatch({ type: 'REMOVE_ROW', rowId });
  }, []);

  const setUploadStatus = useCallback(
    (rowId: string, status: DocumentUploadStatus, progress = 0, objectId?: string) => {
      dispatch({ type: 'SET_UPLOAD_STATUS', rowId, status, progress, objectId });
    },
    []
  );

  const cancleDeleteRequest = useCallback(() => {
    dispatch({ type: 'CANCEL_DELETE' });
  }, []);

  const deleteRow = useCallback((rowId: string) => {
    const controller = uploadAbortControllersRef.current.get(rowId);
    if (controller) {
      controller.abort();
      uploadAbortControllersRef.current.delete(rowId);
      // Clean up the registry entry if the objectId was already assigned by uploadBinaryForRow
      const row = rowsRef.current.find((r) => r.rowId === rowId);
      if (row?.values.objectId) registry?.remove(row.values.objectId);
    }
    dispatch({ type: 'REQUEST_DELETE_ROW', rowId });
  }, [registry]);

  const setValidationError = useCallback((rowId: string, error: string) => {
    dispatch({ type: 'SET_VALIDATION_ERROR', rowId, error });
  }, []);

  const reset = useCallback((initialRows?: InitialFileObjectRow[]) => {
    dispatch({ type: 'RESET', initialRows, activeUploads: registry?.getSnapshot() });
  }, [registry]);

  const confirmDeleteRow = useCallback(async (rowId: string) => {
    // if no fileId present no delete can be done
    if (fileId === undefined) {
      toast.error(
        t('documents.detail.fileObjects.deleteError')
      );

      cancleDeleteRequest();
      return;
    }

    const targetRow = rowsRef.current.find((row) => row.rowId === rowId);
    const objectId = targetRow?.values.objectId;

    // Abort any in-flight upload for this row before proceeding with the delete
    const controller = uploadAbortControllersRef.current.get(rowId);
    if (controller) {
      controller.abort();
      uploadAbortControllersRef.current.delete(rowId);
    }
    if (objectId) registry?.remove(objectId);

    if (!objectId) {
      removeRow(rowId);
      return;
    }

    // set the isDeleting flag to show the loading state
    dispatch({ type: 'SET_IS_DELETING' });

    try {
      await deleteDocumentFileObject(fileId, objectId);

      // remove the ui row, also resets the delete states
      removeRow(rowId)

      // send toast
      toast.success(
        t('documents.detail.fileObjects.deleteSuccess')
      );

      invalidateFileQuery(fileId)
    } catch {
      toast.error(
        t('documents.detail.fileObjects.deleteError')
      );
      // reset the delete states
      cancleDeleteRequest();
    }
  }, [fileId, registry, removeRow, cancleDeleteRequest, t, invalidateFileQuery]);


  // uploadBinaryForRow handles the shared upload lifecycle and status updates for one row/object pair.
  // It also registers progress in the upload registry so status survives sidepanel close/reopen.
  const uploadBinaryForRow = useCallback(
    async (
      rowId: string,
      objectId: string,
      file: File,
      fileId: string,
      signal?: AbortSignal
    ) => {
      if (!fileId) {
        console.error("uploadBinaryForRow: No File Id ")
        return
      }

      setUploadStatus(rowId, 'uploading', 0, objectId);
      registry?.register({ objectId, fileId, fileName: file.name, status: 'uploading', progress: 0 });

      const uploadUrlResponse = await getDocumentFileUploadUrl(fileId, objectId, file.name);
      await uploadDocumentFileBinary(
        uploadUrlResponse.url,
        file,
        (progress) => {
          setUploadStatus(rowId, 'uploading', progress, objectId);
          registry?.updateProgress(objectId, progress);
        },
        signal
      );

      setUploadStatus(rowId, 'uploaded', 100, objectId);
      registry?.complete(objectId);

      // Set active stored file if storedFileId is returned
      if (uploadUrlResponse.storedFileId) {
        await setStoredFileActive(fileId, objectId, uploadUrlResponse.storedFileId);
      }

      toast.success(t('documents.detail.fileObjects.saveSuccess'));
    },
    [registry, setUploadStatus, setStoredFileActive, t]
  );

  // createAndUploadRowObject creates a file object first and then uploads its binary.
  const createAndUploadRowObject = useCallback(
    async (rowId: string, file: File, values: FileObjectValues, fileId: string, signal?: AbortSignal) => {
      if (!fileId) {
        console.error("No file id present")
        toast.error(t('documents.detail.fileObjects.uploadStartError'));
        return
      }

      const created = await createDocumentFileObject(fileId, {
        languageId: values.languageId,
        fileSize: file.size,
        name: values.name ?? "",
        originalFileName: file.name,
      });

      invalidateFileQuery(fileId);

      await uploadBinaryForRow(rowId, created.id, file, fileId, signal);

      invalidateFileQuery(fileId);

      return created
    },
    [uploadBinaryForRow, invalidateFileQuery, t]
  );

  // validate and getRowsForSubmit read from rowsRef to keep stable callback signatures.
  const validate = useCallback(() => {
    dispatch({ type: 'VALIDATE', t });
    const activeRows = getActiveRows(rowsRef.current);
    return activeRows.every((row) => !row.languageError && !row.fileError);
  }, [t]);

  const getRowsForSubmit = useCallback((): FileObjectSubmitRow[] => {
    return getSubmittableRows(rowsRef.current);
  }, []);

  const maybeCreateAndUploadImmediately = useCallback(
    async (rowId: string, pendingUpdates: Partial<FileObjectRow>) => {
      if (!fileId || !saveImmediately) return;

      const row = rowsRef.current.find((r) => r.rowId === rowId);
      if (!row) return;

      const effectiveRow = { ...row, ...pendingUpdates };
      const objectValues = effectiveRow.values;
      if (objectValues.objectId || objectValues.languageId === null || !effectiveRow.file) return;

      const controller = new AbortController();
      uploadAbortControllersRef.current.set(rowId, controller);
      try {
        await createAndUploadRowObject(rowId, effectiveRow.file, objectValues, fileId, controller.signal);
      } catch (error) {
        if (!isAbortError(error)) setUploadStatus(rowId, 'failed', 0);
      } finally {
        uploadAbortControllersRef.current.delete(rowId);
      }
    },
    [fileId, saveImmediately, setUploadStatus, createAndUploadRowObject]
  );

  const save = useCallback(async (file?: DocumentFile): Promise<CreateResult[]> => {
    let usedFileId = fileId ?? file?.id
    if (!usedFileId) {
      console.error("No File ID during save of fileObj")
      return []
    }

    const currentRows = rowsRef.current;

    const result = await Promise.allSettled(
      currentRows.map(async (row) => {
        if (row.changeState === 'deleted' && row.values.objectId) {
          await deleteDocumentFileObject(usedFileId, row.values.objectId);
          toast.success(t('documents.detail.fileObjects.deleteSuccess'));

          return {
            state: row.changeState,
            objectId: row.values.objectId,
            rowId: row.rowId
          } as CreateResult
        }

        if (row.changeState === 'created') {
          if (!row.file) {
            console.error("useFIleObjecRows.save, cant create without a file")
            return {
              state: 'failed',
              rowId: row.rowId
            } as CreateResult
          }

          const created = await createAndUploadRowObject(row.rowId, row.file, row.values, usedFileId);
          if (created === undefined) {
            return {
              state: 'failed',

              rowId: row.rowId
            } as CreateResult
          }
          return {
            state: row.changeState,
            objectId: created.id,
            fileObject: created,
            rowId: row.rowId
          } as CreateResult
        }

        if (row.changeState === 'modified' && row.values.objectId) {
          await updateDocumentFileObject(usedFileId, row.values.objectId, {
            languageId: row.values.languageId,
            name: row.values.name,
            fileSize: row.file?.size,
            originalFileName: row.file?.name,
          });

          return {
            state: row.changeState,
            objectId: row.values.objectId,
            rowId: row.rowId
          } as CreateResult
        }

        return {
          state: 'failed',
          rowId: row.rowId
        } as CreateResult
      })
    );

    return result.map((res) => {
      if (res.status === 'fulfilled' && res.value.state !== 'failed') {
        return res.value
      } else {
        return undefined
      }
    }).filter((val) => val !== undefined)
  },
    [fileId, createAndUploadRowObject, t]
  );

  const updateLanguage = useCallback(
    (rowId: string, languageId: number | null) => {
      dispatch({ type: 'UPDATE_LANGUAGE', rowId, languageId });
      void maybeCreateAndUploadImmediately(rowId, { values: { languageId } });
    },
    [maybeCreateAndUploadImmediately]
  );

  const updateName = useCallback((rowId: string, name: string) => {
    dispatch({ type: 'UPDATE_NAME', rowId, name });
    save();
  }, [save]);

  const updateFile = useCallback(
    (rowId: string, file: File | null) => {
      dispatch({ type: 'UPDATE_FILE', rowId, file });
      if (file) {
        void maybeCreateAndUploadImmediately(rowId, { file });
      }
    },
    [maybeCreateAndUploadImmediately]
  );

  const reuploadFileObject = useCallback(
    async (rowId: string, file: File) => {
      const row = rowsRef.current.find((r) => r.rowId === rowId);
      if (!fileId || !row?.values.objectId) return;

      const objectId = row.values.objectId;
      const controller = new AbortController();
      uploadAbortControllersRef.current.set(rowId, controller);

      try {
        await uploadBinaryForRow(rowId, objectId, file, fileId, controller.signal);
        await updateDocumentFileObject(fileId, objectId, { originalFileName: file.name });
        dispatch({ type: 'UPDATE_ORIGINAL_FILE_NAME', rowId, originalFileName: file.name });
        invalidateFileQuery(fileId);
      } catch (error) {
        if (!isAbortError(error)) {
          setUploadStatus(rowId, 'failed', 0, objectId);
          registry?.fail(objectId);
          throw error;
        }
      } finally {
        uploadAbortControllersRef.current.delete(rowId);
      }
    },
    [fileId, invalidateFileQuery, registry, setUploadStatus, uploadBinaryForRow]
  );

  const onOpenFile = useCallback(
    async (rowId: string, download: boolean) => {
      const row = rowsRef.current.find((r) => r.rowId === rowId);
      if (!fileId || !row?.values.objectId) {
        console.error('File ID or object ID not found', { fileId, row })
        toast.error(t('documents.detail.fileObjects.fileNotFound'));
        return
      }

      try {
        if (download) {
          await downloadDocumentFile({
            fileId,
            objectId: row.values.objectId,
          });
        }
        else {
          await openDocumentFileToView({
            fileId,
            objectId: row.values.objectId
          })
        }
      } catch {
        toast.error(t('documents.detail.fileObjects.downloadError'));
      }
    },
    [fileId, t]
  );




  const actions = useMemo<FileObjectRowsActions>(
    () => ({
      addRowWithLanguage,
      deleteRow,
      confirmDeleteRow,
      cancleDeleteRequest,
      removeRow,
      updateLanguage,
      updateName,
      updateFile,
      setUploadStatus,
      setValidationError,
      validate,
      reset,
      getRowsForSubmit,
      reuploadFileObject,
      save,
      onOpenFile,
    }),
    [
      addRowWithLanguage,
      deleteRow,
      confirmDeleteRow,
      cancleDeleteRequest,
      removeRow,
      updateLanguage,
      updateName,
      updateFile,
      setUploadStatus,
      setValidationError,
      validate,
      reset,
      getRowsForSubmit,
      reuploadFileObject,
      save,
      onOpenFile
    ]
  );

  return { state: deriveState(inner, fileName), actions };
}

// createReducerState builds reducer state from optional initial rows.
// When no initial rows are provided, one blank row is seeded so the single-file-object
// assumption is satisfied from the start.
function createReducerState(initialRows?: InitialFileObjectRow[]): ReducerState {
  const rows = buildInitialRows(initialRows);
  const seededRows = rows.length > 0 ? rows : [buildRow({ values: { languageId: null, originalFileName: "" } }, true)];
  return {
    rows: seededRows,
    validationErrors: {},
    isDeletingObject: false,
    pendingDeleteObjectId: null,
  };
}

// reducer applies all synchronous row state transitions.
function reducer(state: ReducerState, action: ReducerAction): ReducerState {
  switch (action.type) {
    case 'ADD_ROW': {
      const newRow = buildRow({ values: { languageId: action.languageId } }, true);
      return { ...state, rows: [...state.rows, newRow] };
    }

    case 'REMOVE_ROW': {
      return removeRowFromState(state, action.rowId);
    }

    case 'REQUEST_DELETE_ROW': {
      const targetRow = state.rows.find((row) => row.rowId === action.rowId);
      if (!targetRow) return state;

      if (targetRow.changeState !== 'created') {
        return {
          ...state,
          isDeletingObject: false,
          pendingDeleteObjectId: action.rowId,
        };
      }

      return removeRowFromState(state, action.rowId);
    }

    case 'SET_IS_DELETING': {
      return {
        ...state,
        isDeletingObject: true,
      };
    }

    case 'CANCEL_DELETE': {
      return {
        ...state,
        isDeletingObject: false,
        pendingDeleteObjectId: null,
      };
    }

    case 'UPDATE_LANGUAGE': {
      const rows = updateEditableRow(state.rows, action.rowId, (row) => ({
        ...row,
        changeState: row.values.objectId ? 'modified' : row.changeState,
        values: {
          ...row.values,
          languageId: action.languageId,
        },
        languageError: undefined,
      }));

      return { ...state, rows };
    }

    case 'UPDATE_NAME': {
      const rows = updateEditableRow(state.rows, action.rowId, (row) => ({
        ...row,
        changeState: row.values.objectId ? 'modified' : row.changeState,
        values: {
          ...row.values,
          name: action.name,
        },
        fileError: undefined,
      }));

      return { ...state, rows };
    }

    case 'UPDATE_FILE': {
      let fileName = undefined
      if (action.file !== null) {
        fileName = getFileBaseName(action.file.name)
      }

      const rows = updateEditableRow(state.rows, action.rowId, (row) => ({
        ...row,
        file: action.file,
        values: {
          ...row.values,
          name: row.values.name ?? fileName,
          originalFileName: action.file?.name ?? row.values.originalFileName,
        },
        fileError: undefined,
      }));

      const validationErrors = { ...state.validationErrors };
      if (action.file) {
        delete validationErrors[action.rowId];
      }

      return { ...state, rows, validationErrors };
    }

    case 'SET_UPLOAD_STATUS': {
      const { rowId, status, progress, objectId } = action;
      const isUploaded = status === 'uploaded';

      const rows = state.rows.map((row) => {
        if (row.rowId !== rowId) return row;

        return {
          ...row,
          uploadStatus: status,
          uploadProgress: progress,
          values: {
            ...row.values,
            objectId: objectId ?? row.values.objectId,
          },
          ...(isUploaded && {
            changeState: 'unchanged' as const,
            file: null,
            values: {
              ...row.values,
              objectId: objectId ?? row.values.objectId,
              isAvailable: true,
            },
          }),
        };
      });

      return { ...state, rows };
    }

    case 'UPDATE_ORIGINAL_FILE_NAME': {
      const rows = state.rows.map((row) => {
        if (row.rowId !== action.rowId) return row;

        return {
          ...row,
          values: {
            ...row.values,
            originalFileName: action.originalFileName,
          },
        };
      });

      return { ...state, rows };
    }

    case 'SET_VALIDATION_ERROR': {
      return {
        ...state,
        validationErrors: {
          ...state.validationErrors,
          [action.rowId]: action.error,
        },
      };
    }

    case 'VALIDATE': {
      const activeRows = getActiveRows(state.rows);
      const languageCounts = buildLanguageCounts(activeRows);
      const rows = state.rows.map((row) => {
        if (row.changeState === 'deleted') return row;
        return withValidationErrors(row, languageCounts, action.t);
      });

      return { ...state, rows };
    }

    case 'RESET': {
      const newState = createReducerState(action.initialRows);
      if (!action.activeUploads || action.activeUploads.size === 0) return newState;

      // Reconnect rows to any ongoing uploads from a previous hook instance.
      // Matches by objectId (stable server key), skips idle/uploaded entries.
      const rows = newState.rows.map((row) => {
        const objectId = row.values.objectId;
        if (!objectId) return row;
        const entry = action.activeUploads!.get(objectId);
        if (!entry || entry.status === 'idle' || entry.status === 'uploaded') return row;
        return { ...row, uploadStatus: entry.status, uploadProgress: entry.progress };
      });
      return { ...newState, rows };
    }

    default:
      return state;
  }
}

// removeRowFromState removes a created row immediately or marks persisted rows as deleted.
function removeRowFromState(state: ReducerState, rowId: string): ReducerState {
  const targetRow = state.rows.find((row) => row.rowId === rowId);
  if (!targetRow) return state;

  const rows = targetRow.values.objectId
    ? state.rows.map((row) =>
      row.rowId === rowId ? { ...row, changeState: 'deleted' as const } : row
    )
    : state.rows.filter((row) => row.rowId !== rowId);
  const validationErrors = { ...state.validationErrors };
  delete validationErrors[rowId];

  return {
    ...state,
    rows,
    validationErrors,
    isDeletingObject: false,
    pendingDeleteObjectId: null,
  };
}

// deriveState computes consumer-facing flags from the internal reducer state.
function deriveState(innerState: ReducerState, fileName: string): FileObjectRowsState {
  const { rows, validationErrors } = innerState;
  const activeRows = getActiveRows(rows);
  const selectedLanguages = activeRows
    .filter((row) => row.values.languageId)
    .map((row) => row.values.languageId);

  const hasValidationErrors = Object.keys(validationErrors).length > 0;
  const hasDuplicateLanguages = selectedLanguages.length !== new Set(selectedLanguages).size;
  const allRowsComplete = activeRows.every((row) => isRowComplete(row));

  const result = {
    rows: rows.map((row) => {

      let name = row.values.name
      if (name === undefined || name === "") {
        name = fileName
      }
      return {
        ...row,
        values: {
          ...row.values,
          name: name,
        },
      }
    }),
    validationErrors,
    pendingDeleteObjectId: innerState.pendingDeleteObjectId,
    isDeletingObject: innerState.isDeletingObject,
    isDirty: rows.some((row) => row.changeState !== 'unchanged'),
    isValid:
      !hasValidationErrors &&
      !hasDuplicateLanguages &&
      activeRows.length > 0 &&
      allRowsComplete,
    hasUploadingRows: rows.some((row) => row.uploadStatus === 'uploading'),
    hasFailedRows: rows.some((row) => row.uploadStatus === 'failed'),
  };

  return result
}

// buildRow creates one reducer row from initial row data.
function buildRow(initial?: InitialFileObjectRow, isNew = true): FileObjectRow {
  return {
    rowId: newUUID(),
    values: {
      objectId: initial?.values.objectId,
      languageId: initial?.values.languageId ?? null,
      isAvailable: initial?.values.isAvailable,
      name: initial?.values.name,
      originalFileName: initial?.values.originalFileName,
    },
    file: initial?.file ?? null,
    changeState: isNew ? 'created' : 'unchanged',
    uploadStatus: 'idle',
    uploadProgress: 0,
  };
}

// buildInitialRows maps incoming initial rows into reducer rows.
function buildInitialRows(initialRows?: InitialFileObjectRow[]): FileObjectRow[] {
  if (!initialRows || initialRows.length === 0) return [];
  return initialRows.map((initialRow) => buildRow(initialRow, !initialRow.values.objectId));
}

// getActiveRows returns rows that are not marked for deletion.
function getActiveRows(rows: FileObjectRow[]): FileObjectRow[] {
  return rows.filter((row) => row.changeState !== 'deleted');
}

// getSubmittableRows maps changed rows into submit payload shape.
function getSubmittableRows(rows: FileObjectRow[]): FileObjectSubmitRow[] {
  return rows
    .filter((row) => row.changeState !== 'unchanged' && row.values.languageId !== null)
    .map((row) => ({
      rowId: row.rowId,
      values: row.values,
      file: row.file,
      changeState: row.changeState,
    }));
}

// updateEditableRow applies a row patch only when the targeted row is not uploading.
function updateEditableRow(
  rows: FileObjectRow[],
  rowId: string,
  updater: (row: FileObjectRow) => FileObjectRow
): FileObjectRow[] {
  return rows.map((row) => {
    if (row.rowId !== rowId) return row;
    if (row.uploadStatus === 'uploading') return row;

    const updatedRow = updater(row);
    return { ...updatedRow, changeState: computeChangeState(updatedRow) };
  });
}

// computeChangeState keeps row lifecycle state consistent with row values.
function computeChangeState(row: FileObjectRow): FileObjectRow['changeState'] {
  if (row.changeState === 'deleted') return 'deleted';
  if (!row.values.objectId) return 'created';
  if (row.changeState === 'created') return 'created';
  if (row.changeState === 'modified') return 'modified';
  return row.file !== null ? 'modified' : 'unchanged';
}

// buildLanguageCounts returns language usage counts for duplicate validation.
function buildLanguageCounts(activeRows: FileObjectRow[]): Record<string, number> {
  return activeRows
    .filter((row) => row.values.languageId)
    .map((row) => row.values.languageId)
    .reduce(
      (counts, languageId) => {
        if (languageId === null) return counts;
        counts[languageId] = (counts[languageId] || 0) + 1;
        return counts;
      },
      {} as Record<string, number>
    );
}

// withValidationErrors applies language and file errors to one row.
function withValidationErrors(
  row: FileObjectRow,
  languageCounts: Record<string, number>,
  t: TFunction
): FileObjectRow {
  const isDuplicateLanguage =
    row.values.languageId !== null && (languageCounts[row.values.languageId] ?? 0) > 1;
  const needsFile = row.changeState === 'created' || (!row.values.name && !row.file);
  const hasFile = Boolean(row.file) || !needsFile;

  return {
    ...row,
    languageError: row.values.languageId
      ? isDuplicateLanguage
        ? t('documents.createFile.validation.duplicateLanguage')
        : undefined
      : t('documents.createFile.validation.languageRequired'),
    fileError: hasFile ? undefined : t('documents.createFile.validation.fileRequired'),
  };
}

// isAbortError returns true when an error was caused by an AbortController signal.
// Used to distinguish intentional cancellations from real upload failures.
function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.message === 'Upload aborted';
}

// isRowComplete verifies whether a row contains all required submit values.
function isRowComplete(row: FileObjectRow): boolean {
  const hasFile = row.file !== null || (row.changeState !== 'created' && Boolean(row.values.name));
  return hasFile;
}

interface UseFileObjectRowsOptions {
  initialRows?: InitialFileObjectRow[];
  fileId?: string;
  saveImmediately?: boolean;
}

interface ReducerState {
  rows: FileObjectRow[];
  validationErrors: Record<string, string>;
  isDeletingObject: boolean;
  pendingDeleteObjectId: string | null;
}

type ReducerAction =
  | { type: 'ADD_ROW'; languageId: number | null }
  | { type: 'REMOVE_ROW'; rowId: string }
  | { type: 'REQUEST_DELETE_ROW'; rowId: string }
  | { type: 'SET_IS_DELETING' }
  | { type: 'CANCEL_DELETE' }
  | { type: 'UPDATE_LANGUAGE'; rowId: string; languageId: number | null }
  | { type: 'UPDATE_NAME'; rowId: string; name: string }
  | { type: 'UPDATE_FILE'; rowId: string; file: File | null }
  | { type: 'UPDATE_ORIGINAL_FILE_NAME'; rowId: string; originalFileName: string }
  | {
    type: 'SET_UPLOAD_STATUS';
    rowId: string;
    status: DocumentUploadStatus;
    progress: number;
    objectId?: string;
  }
  | { type: 'SET_VALIDATION_ERROR'; rowId: string; error: string }
  | { type: 'VALIDATE'; t: TFunction }
  | { type: 'RESET'; initialRows?: InitialFileObjectRow[]; activeUploads?: ReadonlyMap<string, ActiveUpload> };



